import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoBack } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import api, { SERVER_URL } from '../../utils/api';
import RichTextEditor from '../../components/RichTextEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faPaperclip, faTags, faRupeeSign, faCreditCard, faInfoCircle, faListAlt, faCheckCircle, faFilePdf, faFileVideo, faBuilding, faUser, faSpinner, faPlus, faSearch, faTrash, faChevronDown, faChevronUp, faLink } from '@fortawesome/free-solid-svg-icons';
import imageCompression from 'browser-image-compression';
import SearchSelect from '../../components/SearchSelect'; // 👇 NEW: Imported Component
import '../../styles/App.css';
import '../../styles/expenses.css';

const EditExpense = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const goBack = useGoBack('/expenses');
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const userRole = currentUser?.role || 'EMPLOYEE';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isCompressing, setIsCompressing] = useState(false);

    const [expenseStatus, setExpenseStatus] = useState('');
    const [adminFeedback, setAdminFeedback] = useState('');

    const [usersList, setUsersList] = useState([]);
    const [allEmployeesList, setAllEmployeesList] = useState([]);
    const [projectsList, setProjectsList] = useState([]);
    const [vendorsList, setVendorsList] = useState([]); 

    const [systemSettings, setSystemSettings] = useState({
        inventoryCatAThreshold: 500,
        inventoryCatBThreshold: 100
    });

    const [formData, setFormData] = useState({
        expenseType: 'Project Expense',
        category: 'Product / Item Purchase',
        expenseDate: '',
        amount: '',
        paymentSourceId: '',
        projectName: '',
        descriptionTags: '',
        vendorId: '', 
        isCompanyPayment: false 
    });

    const defaultProduct = {
        productName: '', quantity: 1, unitPrice: '', expiryDate: '', storageLocation: '',
        inventoryItemStatus: 'Available', inventoryAssignedTo: '', isLinkedItem: false
    };

    const [productList, setProductList] = useState([{ ...defaultProduct }]);
    const [expandedItemIndex, setExpandedItemIndex] = useState(0);

    const [expenseDetails, setExpenseDetails] = useState({
        vehicleType: 'Car', vehicleNumber: '', odometerBefore: '', odometerAfter: '', kmTraveled: '', travelFrom: '', travelTo: '', purpose: '',
        travelMode: 'Flight', distanceKm: '', bookingReference: '',
        
        restaurantName: '', foodItemsOrdered: '', numberOfPeople: '',
        hotelName: '', city: '', checkInDate: '', checkOutDate: '', numberOfNights: '',
        
        vendorName: '', billingCycle: 'Monthly', expenseDescription: '', participantName: '', 
        gstNumber: '', 
        paymentDate: '', 

        utilityType: 'Electricity', billingMonth: '', invoiceNumber: '',
        repairType: 'Equipment / IT', serviceProvider: '', warrantyIncluded: 'No'
    });

    const [existingFiles, setExistingFiles] = useState({ paymentScreenshotUrls: [], expenseMediaUrls: [] });
    const [newFiles, setNewFiles] = useState({ paymentScreenshots: [], expenseMedia: [] });

    useEffect(() => {
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchInitialData = async () => {
        try {
            const [userRes, allEmpRes, projRes, venRes, settingsRes] = await Promise.all([
                api.get('/employees/payment-sources'),
                api.get('/employees/directory').catch(() => ({ data: [] })),
                api.get('/projects'),
                api.get('/vendors').catch(() => ({ data: [] })),
                api.get('/settings').catch(() => ({ data: {} }))
            ]);

            setUsersList(userRes.data);
            const employeeArray = Array.isArray(allEmpRes.data) ? allEmpRes.data : (allEmpRes.data?.data || []);
            setAllEmployeesList(employeeArray);
            setProjectsList(projRes.data);
            setVendorsList(venRes.data);

            if (settingsRes.data) {
                setSystemSettings({
                    inventoryCatAThreshold: settingsRes.data.inventoryCatAThreshold || 500,
                    inventoryCatBThreshold: settingsRes.data.inventoryCatBThreshold || 100
                });
            }

            const res = await api.get(`/expenses/${id}`);
            const data = res.data;

            setExpenseStatus(data.status || '');
            setAdminFeedback(data.adminFeedback || '');

            setFormData({
                expenseType: data.expenseType || 'Project Expense',
                category: data.category || 'Product / Item Purchase',
                expenseDate: data.expenseDate ? new Date(data.expenseDate).toISOString().split('T')[0] : '',
                amount: data.amount || '',
                paymentSourceId: data.paymentSourceId?._id || data.paymentSourceId || currentUser.id,
                projectName: data.projectName || '',
                descriptionTags: data.descriptionTags || '',
                vendorId: data.vendorId?._id || data.vendorId || '', 
                isCompanyPayment: data.isCompanyPayment || false 
            });

            if (data.expenseDetails) {
                setExpenseDetails(prev => ({ ...prev, ...data.expenseDetails }));

                // Products live either in items[] (current) or flat on expenseDetails
                // (legacy). Hydrate both into the list so saving never drops items.
                const d = data.expenseDetails;
                let loadedProducts = [];

                if (Array.isArray(d.items) && d.items.length > 0) {
                    loadedProducts = d.items.filter(Boolean).map(item => ({ ...defaultProduct, ...item }));
                } else if (d.productName || d.itemName) {
                    loadedProducts = [{
                        ...defaultProduct,
                        productName: d.productName || d.itemName,
                        quantity: d.quantity || 1,
                        unitPrice: d.unitPrice || '',
                        inventoryItemStatus: d.inventoryItemStatus || 'Available',
                        storageLocation: d.storageLocation || '',
                        inventoryAssignedTo: d.inventoryAssignedTo || '',
                        expiryDate: d.expiryDate || ''
                    }];
                }

                if (loadedProducts.length > 0) setProductList(loadedProducts);
            }

            setExistingFiles({
                paymentScreenshotUrls: data.paymentScreenshotUrls?.length > 0 ? data.paymentScreenshotUrls : (data.paymentScreenshotUrl ? [data.paymentScreenshotUrl] : []),
                expenseMediaUrls: data.expenseMediaUrls || []
            });

        } catch (err) {
            Swal.fire('Error', 'Could not load expense details', 'error');
            navigate('/expenses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (formData.category === 'Fuel Expense (Car / Bike)') {
            const before = parseFloat(expenseDetails.odometerBefore) || 0;
            const after = parseFloat(expenseDetails.odometerAfter) || 0;
            if (after > before) setExpenseDetails(prev => ({ ...prev, kmTraveled: after - before }));
        }
        if (formData.category === 'Accommodation') {
            if (expenseDetails.checkInDate && expenseDetails.checkOutDate) {
                const checkIn = new Date(expenseDetails.checkInDate);
                const checkOut = new Date(expenseDetails.checkOutDate);
                const diffTime = Math.abs(checkOut - checkIn);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                setExpenseDetails(prev => ({ ...prev, numberOfNights: diffDays }));
            }
        }
    }, [expenseDetails.odometerBefore, expenseDetails.odometerAfter, expenseDetails.checkInDate, expenseDetails.checkOutDate, formData.category]);

    const handleMainChange = (e) => {
        const { name, value, type, checked } = e.target;
        let updatedData = { ...formData };
        if (type === 'checkbox') { updatedData[name] = checked; } else { updatedData[name] = value; }

        if (name === 'expenseType') {
            if (value === 'Project Expense' && updatedData.category === 'Regular Office Expense') {
                updatedData.category = 'Product / Item Purchase';
            } else if (value === 'Regular Office Expense' && ['Participant Payment', 'Vendor Payment'].includes(updatedData.category)) {
                updatedData.category = 'Regular Office Expense';
            }
        }
        setFormData(updatedData);
    };

    const handleDetailChange = (e) => setExpenseDetails({ ...expenseDetails, [e.target.name]: e.target.value });

    const handleProductChange = (index, field, value) => {
        const updatedList = [...productList];
        updatedList[index] = { ...updatedList[index], [field]: value };

        if (field === 'inventoryItemStatus') {
            if (value === 'Available') updatedList[index].inventoryAssignedTo = '';
            if (value === 'Assigned') updatedList[index].storageLocation = '';
            if (value === 'Do Not Track' || value === 'Linked') {
                updatedList[index].storageLocation = '';
                updatedList[index].inventoryAssignedTo = '';
            }
        }
        setProductList(updatedList);
    };

    const addProduct = () => {
        setProductList([...productList, { ...defaultProduct }]);
        setExpandedItemIndex(productList.length);
    };

    const removeProduct = (index) => {
        if (productList.length === 1) return Swal.fire('Wait', 'You must have at least one product in the list.', 'info');
        setProductList(productList.filter((_, i) => i !== index));

        if (expandedItemIndex === index) {
            setExpandedItemIndex(Math.max(0, index - 1));
        } else if (expandedItemIndex > index) {
            setExpandedItemIndex(expandedItemIndex - 1);
        }
    };

    // Helper to generate the Payer List based on roles
    const getPaymentSourceOptions = () => {
        const optionsPool = (userRole === 'ADMIN' || userRole === 'ACCOUNTS') ? allEmployeesList : usersList;
        const currentUserId = currentUser.id || currentUser._id;
        const filteredPool = optionsPool.filter(u => String(u._id) !== String(currentUserId));
        return [ { _id: currentUserId, name: 'Myself (Reimburse Me)', role: userRole }, ...filteredPool ];
    };

    const removeExistingFile = (fieldName, index) => {
        setExistingFiles(prev => ({
            ...prev,
            [fieldName]: prev[fieldName].filter((_, i) => i !== index)
        }));
    };

    const removeNewFile = (fieldName, index) => {
        setNewFiles(prev => ({
            ...prev,
            [fieldName]: prev[fieldName].filter((_, i) => i !== index)
        }));
    };

    // Matches the server (routes/expenses.js): more than this per save is rejected.
    const MAX_FILES = { paymentScreenshots: 10, expenseMedia: 10 };
    const FILE_LABEL = { paymentScreenshots: 'payment screenshots', expenseMedia: 'bills or media files' };

    const handleFileChange = async (e, fieldName) => {
        let selectedFiles = Array.from(e.target.files);
        const limit = MAX_FILES[fieldName];
        if (limit && selectedFiles.length > limit) {
            Swal.fire('Too many files', `You can attach up to ${limit} ${FILE_LABEL[fieldName]} at a time. Only the first ${limit} were added.`, 'warning');
            selectedFiles = selectedFiles.slice(0, limit);
        }
        const processedFiles = [];
        setIsCompressing(true);

        for (let file of selectedFiles) {
            const isImage = file.type.startsWith('image/') || file.name.match(/\.(jpg|jpeg|png|gif|heic|heif)$/i);
            if (isImage) {
                try {
                    const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: false, fileType: 'image/jpeg' };
                    const compressedBlob = await imageCompression(file, options);
                    const safeName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
                    const safelyNamedFile = new File([compressedBlob], safeName, { type: 'image/jpeg', lastModified: Date.now() });
                    processedFiles.push(safelyNamedFile);
                } catch (error) {
                    console.error("Error compressing image:", error);
                    processedFiles.push(file);
                }
            } else if (file.type.startsWith('video/') && file.size > 15 * 1024 * 1024) {
                Swal.fire('Too Large', `Video "${file.name}" is larger than 15MB.`, 'warning');
            } else {
                processedFiles.push(file);
            }
        }
        setNewFiles(prev => ({ ...prev, [fieldName]: processedFiles }));
        setIsCompressing(false);
    };

    const handleAddVendor = async () => {
        const { value: formValues } = await Swal.fire({
            title: 'Add New Vendor',
            html: `
                <div style="display: flex; flex-direction: column; gap: 10px; text-align: left;">
                    <input id="swal-vname" class="swal2-input m-0" placeholder="Vendor Name *" required>
                    <input id="swal-vaddress" class="swal2-input m-0" placeholder="Vendor Address *" required>
                    <input id="swal-vgst" class="swal2-input m-0" placeholder="GST Number (Optional)" style="text-transform: uppercase;">
                    <input id="swal-vnotes" class="swal2-input m-0" placeholder="Notes / Bank Details (Optional)">
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Save Vendor',
            confirmButtonColor: '#16a34a',
            preConfirm: () => {
                const name = document.getElementById('swal-vname').value;
                const address = document.getElementById('swal-vaddress').value;
                if (!name || !address) {
                    Swal.showValidationMessage('Name and Address are required!');
                    return false;
                }
                return {
                    name, address,
                    gstNumber: document.getElementById('swal-vgst').value,
                    notes: document.getElementById('swal-vnotes').value
                };
            }
        });

        if (formValues) {
            try {
                const res = await api.post('/vendors', formValues);
                const updatedList = [...vendorsList, res.data].sort((a, b) => a.name.localeCompare(b.name));
                setVendorsList(updatedList);
                setFormData(prev => ({ ...prev, vendorId: res.data._id }));
                Swal.fire({ icon: 'success', title: 'Vendor Added!', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Failed to add vendor', 'error');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.amount || !formData.descriptionTags) return Swal.fire('Required Fields', 'Amount and Description are required.', 'warning');
        if (formData.category === 'Vendor Payment' && !formData.vendorId) return Swal.fire('Missing Vendor', 'Please select a Vendor for this payment.', 'warning');

        if (formData.category === 'Product / Item Purchase') {
            for (let i = 0; i < productList.length; i++) {
                const prod = productList[i];

                if (!prod.productName || !prod.unitPrice || !prod.quantity) {
                    return Swal.fire('Missing Details', `Please fill Product Name, Quantity, and Price for Item #${i + 1}`, 'warning');
                }

                if (prod.isLinkedItem) continue;

                if (prod.inventoryItemStatus === 'Assigned' && !prod.inventoryAssignedTo) {
                    return Swal.fire('Missing Employee', `Please select who Item #${i + 1} is assigned to.`, 'warning');
                }
                if (prod.inventoryItemStatus === 'Available' && !prod.storageLocation) {
                    return Swal.fire('Missing Location', `Please provide a storage location for Item #${i + 1}.`, 'warning');
                }

                if (['Available', 'Assigned'].includes(prod.inventoryItemStatus) && Number(prod.unitPrice) >= systemSettings.inventoryCatAThreshold) {
                    const hasNewFiles = newFiles.expenseMedia && newFiles.expenseMedia.length > 0;
                    const hasExistingFiles = existingFiles.expenseMediaUrls && existingFiles.expenseMediaUrls.length > 0;
                    if (!hasNewFiles && !hasExistingFiles) {
                        return Swal.fire({
                            title: 'Image Required (Category A)',
                            text: `Item #${i + 1} costs ₹${systemSettings.inventoryCatAThreshold} or more (Category A). You MUST upload a photo proof of the product to proceed.`,
                            icon: 'warning',
                            confirmButtonColor: '#215D7B'
                        });
                    }
                }
            }
        }

        setSaving(true);
        setUploadProgress(0);

        try {
            const data = new FormData();
            Object.keys(formData).forEach(key => data.append(key, formData[key]));
            
            let relevantDetails = {};
            const d = expenseDetails;
            
            switch (formData.category) {
                case 'Vendor Payment': relevantDetails = { paymentDate: d.paymentDate }; break;
                case 'Participant Payment': relevantDetails = { participantName: d.participantName }; break;
                case 'Product / Item Purchase': relevantDetails = { items: productList }; break;
                case 'Fuel Expense (Car / Bike)': relevantDetails = { vehicleType: d.vehicleType, vehicleNumber: d.vehicleNumber, travelFrom: d.travelFrom, travelTo: d.travelTo, odometerBefore: d.odometerBefore, odometerAfter: d.odometerAfter, kmTraveled: d.kmTraveled, purpose: d.purpose }; break;
                case 'Food Expense': relevantDetails = { restaurantName: d.restaurantName, foodItemsOrdered: d.foodItemsOrdered, numberOfPeople: d.numberOfPeople }; break;
                case 'Travel Expense': relevantDetails = { travelMode: d.travelMode, distanceKm: d.distanceKm, travelFrom: d.travelFrom, travelTo: d.travelTo, bookingReference: d.bookingReference, purpose: d.purpose }; break;
                case 'Accommodation': relevantDetails = { hotelName: d.hotelName, city: d.city, bookingReference: d.bookingReference, checkInDate: d.checkInDate, checkOutDate: d.checkOutDate, numberOfNights: d.numberOfNights }; break;
                case 'Regular Office Expense': relevantDetails = { vendorName: d.vendorName, billingCycle: d.billingCycle, expenseDescription: d.expenseDescription }; break;
                case 'Utility / Bills': relevantDetails = { utilityType: d.utilityType, billingMonth: d.billingMonth, invoiceNumber: d.invoiceNumber }; break;
                case 'Maintenance & Repairs': relevantDetails = { repairType: d.repairType, serviceProvider: d.serviceProvider, warrantyIncluded: d.warrantyIncluded }; break;
                default: relevantDetails = {};
            }

            if (d.gstNumber && d.gstNumber.trim() !== '') relevantDetails.gstNumber = d.gstNumber.toUpperCase().trim();

            data.append('expenseDetails', JSON.stringify(relevantDetails));

            data.append('keepPaymentUrls', JSON.stringify(existingFiles.paymentScreenshotUrls));
            data.append('keepMediaUrls', JSON.stringify(existingFiles.expenseMediaUrls));

            if (newFiles.paymentScreenshots && newFiles.paymentScreenshots.length > 0) {
                for (let i = 0; i < newFiles.paymentScreenshots.length; i++) data.append('paymentScreenshots', newFiles.paymentScreenshots[i]);
            }
            if (newFiles.expenseMedia && newFiles.expenseMedia.length > 0) {
                for (let i = 0; i < newFiles.expenseMedia.length; i++) data.append('expenseMedia', newFiles.expenseMedia[i]);
            }

            await api.put(`/expenses/${id}`, data, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            Swal.fire({ icon: 'success', title: expenseStatus === 'Returned' ? 'Resubmitted' : 'Update Saved', timer: 1500, showConfirmButton: false });
            goBack();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Failed to update expense', 'error');
        } finally {
            setSaving(false);
            setUploadProgress(0);
        }
    };

    const getFileUrl = (url) => url ? (url.startsWith('http') ? url : `${SERVER_URL}${url}`) : '';

    const viewSingleFile = (url, title) => {
        const fullUrl = getFileUrl(url);
        const isVideo = url.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
        const isPdf = url.toLowerCase().endsWith('.pdf');

        if (isVideo) {
            Swal.fire({ title, html: `<video src="${fullUrl}" controls style="width:100%; border-radius:6px; max-height:400px; background:#000;"></video>`, width: '800px', showCloseButton: true, showConfirmButton: false });
        } else if (isPdf) {
            Swal.fire({ title, html: `<iframe src="${fullUrl}" width="100%" height="500px" style="border: none; border-radius: 6px;"></iframe>`, width: '800px', showCloseButton: true, showConfirmButton: false });
        } else {
            Swal.fire({ title, imageUrl: fullUrl, imageAlt: title, width: '800px', showCloseButton: true, showConfirmButton: false });
        }
    };

    const renderThumbnail = (url, index, titlePrefix, onRemove) => {
        const fullUrl = getFileUrl(url);
        const isPdf = url.toLowerCase().endsWith('.pdf');
        const isVideo = url.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);

        const removeBtn = (
            <button
                type="button"
                className="file-remove-btn"
                onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                title="Remove file"
            >
                ✕
            </button>
        );

        if (!isPdf && !isVideo) {
            return (
                <div key={index} className="existing-file-card">
                    {removeBtn}
                    <img src={fullUrl} alt="Thumbnail" className="existing-file-thumb" onClick={() => viewSingleFile(url, `${titlePrefix} ${index + 1}`)} />
                </div>
            );
        }

        const iconContent = isPdf
            ? <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '32px', color: 'light-dark(#dc2626, var(--theme-danger-ink))' }} />
            : <FontAwesomeIcon icon={faFileVideo} style={{ fontSize: '32px', color: 'light-dark(#2563eb, var(--theme-blue-ink, #91b7ff))' }} />;

        return (
            <div key={index} className="existing-file-card icon-card" onClick={() => viewSingleFile(url, `${titlePrefix} ${index + 1}`)}>
                {removeBtn}
                {iconContent}
                <span className="file-type-label">{isPdf ? 'PDF' : 'VIDEO'}</span>
            </div>
        );
    };

    const getMediaLabel = () => {
        switch (formData.category) {
            case 'Vendor Payment': return 'Upload Vendor Invoice / Bill (Required)'; 
            case 'Fuel Expense (Car / Bike)': return 'Odometer Image(s) / Fuel Station Receipt';
            case 'Food Expense': return 'Order Screenshot / Restaurant Bill';
            case 'Travel Expense': return 'Travel Receipt / Ticket Screenshot';
            case 'Accommodation': return 'Hotel Receipt / Invoice';
            case 'Product / Item Purchase':
                if (productList.some(p => ['Available', 'Assigned'].includes(p.inventoryItemStatus) && Number(p.unitPrice) >= systemSettings.inventoryCatAThreshold)) {
                    return 'Product Photo(s) / Video(s) (Required for Cat A)';
                }
                return 'Product Photo(s) / Video(s) (Optional)';
            case 'Utility / Bills': return 'Utility Bill / Invoice';
            case 'Maintenance & Repairs': return 'Service Receipt / Invoice';
            case 'Regular Office Expense': return 'Supporting Document / Bill';
            case 'Participant Payment': return 'Payment Receipt / Acknowledgment';
            default: return 'Expense Media (Photos/Videos)';
        }
    };

    const renderCategoryFields = () => {
        switch (formData.category) {
            case 'Vendor Payment':
                return (
                    <>
                        <div className="form-group grid-span-2">
                            <label className="input-label"><FontAwesomeIcon icon={faBuilding} /> Select Vendor *</label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                {/* 👇 NEW: Search Select for Vendors */}
                                <SearchSelect 
                                    options={vendorsList}
                                    recentKey="expense-vendor"
                                    value={formData.vendorId}
                                    onChange={(val) => setFormData({ ...formData, vendorId: val })}
                                    placeholder="Search by vendor name or GST..."
                                    secondaryKey="gstNumber"
                                    icon={faSearch}
                                />
                                <button type="button" className="gts-btn success btn-small m-0" onClick={handleAddVendor} title="Add New Vendor" style={{ height: '42px', whiteSpace: 'nowrap' }}>
                                    <FontAwesomeIcon icon={faPlus} /> Add New
                                </button>
                            </div>
                        </div>

                        {formData.isCompanyPayment && (
                            <div className="form-group grid-span-2">
                                <label className="input-label" style={{ color: 'light-dark(#16a34a, var(--theme-success-ink))' }}>Date of Payment (Accounts) - <span style={{fontWeight: 'normal', color: 'light-dark(#64748b, var(--theme-muted))'}}>If already paid via Company Card</span></label>
                                <input className="custom-input" type="date" name="paymentDate" value={expenseDetails.paymentDate} onChange={handleDetailChange} />
                            </div>
                        )}
                    </>
                );
            case 'Participant Payment':
                return (
                    <div className="form-group grid-span-2">
                        <label className="input-label"><FontAwesomeIcon icon={faUser} /> Participant Name *</label>
                        <input className="custom-input" name="participantName" value={expenseDetails.participantName} onChange={handleDetailChange} placeholder="Enter the participant's full name" required />
                    </div>
                );
            case 'Product / Item Purchase':
                return (
                    <div className="grid-span-2" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {productList.map((prod, index) => {
                            const isExpanded = expandedItemIndex === index;

                            return (
                                <div key={index} style={{ background: 'light-dark(#f8fafc, var(--theme-surface-soft))', border: '1px solid light-dark(#e2e8f0, var(--theme-border))', borderRadius: '8px', overflow: 'hidden' }}>

                                    <div
                                        onClick={() => setExpandedItemIndex(isExpanded ? -1 : index)}
                                        style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '15px 20px', cursor: 'pointer',
                                            background: isExpanded ? 'light-dark(#eff6ff, var(--theme-info-soft, #1d2c3a))' : 'light-dark(#f8fafc, var(--theme-surface-soft, #202327))',
                                            borderBottom: isExpanded ? '1px solid #cbd5e1' : 'none',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        <div>
                                            <h4 style={{ margin: 0, color: 'light-dark(#0f172a, var(--theme-ink))', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                Item #{index + 1} {prod.productName ? <span style={{ color: 'light-dark(#64748b, var(--theme-muted))', fontWeight: 'normal' }}>— {prod.productName}</span> : ''}
                                                {prod.isLinkedItem && <span style={{ background: 'light-dark(#dcfce7, var(--theme-success-soft))', color: 'light-dark(#16a34a, var(--theme-success-ink))', fontSize: '10px', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}><FontAwesomeIcon icon={faLink} /> Linked to Office DB</span>}
                                            </h4>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            {productList.length > 1 && (
                                                <button type="button" onClick={(e) => { e.stopPropagation(); removeProduct(index); }} style={{ background: 'none', border: 'none', color: 'light-dark(#dc2626, var(--theme-danger-ink))', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                                                    <FontAwesomeIcon icon={faTrash} /> Remove
                                                </button>
                                            )}
                                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ color: 'light-dark(#64748b, var(--theme-muted))' }} />
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div style={{ padding: '20px' }}>
                                            <div className="expense-grid">
                                                <div className="form-group grid-span-2">
                                                    <label className="input-label">Product Name *</label>
                                                    <input className="custom-input" value={prod.productName} onChange={(e) => handleProductChange(index, 'productName', e.target.value)} disabled={prod.isLinkedItem} required />
                                                </div>
                                                <div className="form-group"><label className="input-label">Quantity *</label><input className="custom-input" type="number" min="1" value={prod.quantity} onChange={(e) => handleProductChange(index, 'quantity', e.target.value)} required /></div>
                                                <div className="form-group"><label className="input-label">Unit Price (₹) *</label><input className="custom-input" type="number" value={prod.unitPrice} onChange={(e) => handleProductChange(index, 'unitPrice', e.target.value)} required /></div>

                                                {!prod.isLinkedItem ? (
                                                    <>
                                                        <div className="form-group grid-span-2">
                                                            <label className="input-label" style={{ color: 'light-dark(#0f172a, var(--theme-ink))' }}>Will this item be added to Company Inventory?</label>
                                                            <div className="expense-type-toggle" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                <label className={prod.inventoryItemStatus === 'Available' ? 'active' : ''} style={{ width: '100%', justifyContent: 'flex-start' }}>
                                                                    <input type="radio" value="Available" checked={prod.inventoryItemStatus === 'Available'} onChange={(e) => handleProductChange(index, 'inventoryItemStatus', e.target.value)} /> Yes, Keep in Office (Available)
                                                                </label>
                                                                <label className={prod.inventoryItemStatus === 'Assigned' ? 'active' : ''} style={{ width: '100%', justifyContent: 'flex-start' }}>
                                                                    <input type="radio" value="Assigned" checked={prod.inventoryItemStatus === 'Assigned'} onChange={(e) => handleProductChange(index, 'inventoryItemStatus', e.target.value)} /> Yes, Assign to Employee
                                                                </label>
                                                                <label className={prod.inventoryItemStatus === 'Do Not Track' ? 'active' : ''} style={{ width: '100%', justifyContent: 'flex-start', background: prod.inventoryItemStatus === 'Do Not Track' ? 'light-dark(#f1f5f9, var(--theme-surface-raised, #292c31))' : '', color: prod.inventoryItemStatus === 'Do Not Track' ? 'light-dark(#64748b, var(--theme-muted, #959ba5))' : '' }}>
                                                                    <input type="radio" value="Do Not Track" checked={prod.inventoryItemStatus === 'Do Not Track'} onChange={(e) => handleProductChange(index, 'inventoryItemStatus', e.target.value)} /> No, Consumable / Do Not Track
                                                                </label>
                                                            </div>
                                                        </div>

                                                        <div className="form-group grid-span-2" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: 0 }}>
                                                            {prod.inventoryItemStatus === 'Available' && (
                                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                                    <label className="input-label">Storage Location *</label>
                                                                    <input className="custom-input" value={prod.storageLocation} onChange={(e) => handleProductChange(index, 'storageLocation', e.target.value)} placeholder="e.g. Office Room A" required />
                                                                </div>
                                                            )}

                                                            {prod.inventoryItemStatus === 'Assigned' && (
                                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                                    <label className="input-label">Assign To Employee *</label>
                                                                    <SearchSelect
                                                                        options={allEmployeesList}
                                                                        recentKey="inventory-assignee"
                                                                        value={prod.inventoryAssignedTo}
                                                                        onChange={(val) => handleProductChange(index, 'inventoryAssignedTo', val)}
                                                                        placeholder="Search by name or role..."
                                                                        secondaryKey="role"
                                                                        icon={faUser}
                                                                    />
                                                                </div>
                                                            )}

                                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                                <label className="input-label">Expiry Date (If applicable)</label>
                                                                <input className="custom-input" type="date" value={prod.expiryDate} onChange={(e) => handleProductChange(index, 'expiryDate', e.target.value)} />
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="form-group grid-span-2">
                                                        <div style={{ background: 'light-dark(#f0fdf4, var(--theme-success-soft))', padding: '15px', borderRadius: '8px', border: '1px dashed #22c55e', color: 'light-dark(#16a34a, var(--theme-success-ink))', fontWeight: '600', fontSize: '13px' }}>
                                                            <FontAwesomeIcon icon={faCheckCircle} /> This item's physical location is already tracked in the database. No further inventory details are needed.
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                            <button type="button" className="gts-btn doc-btn" style={{ background: 'light-dark(#eff6ff, var(--theme-accent-soft))', color: 'light-dark(#2563eb, var(--theme-blue-ink, #91b7ff))', border: '1px dashed light-dark(#bfdbfe, var(--theme-border-strong, #4a4e55))' }} onClick={addProduct}>
                                <FontAwesomeIcon icon={faPlus} /> Add New Product
                            </button>
                        </div>
                    </div>
                );
            case 'Utility / Bills':
                return (
                    <>
                        <div className="form-group">
                            <label className="input-label">Utility Type *</label>
                            <select className="swal2-select custom-select" name="utilityType" value={expenseDetails.utilityType} onChange={handleDetailChange}>
                                <option value="Electricity">Electricity</option>
                                <option value="Internet / Wi-Fi">Internet / Wi-Fi</option>
                                <option value="Water">Water</option>
                                <option value="Software Subscription">Software Subscription</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="form-group"><label className="input-label">Billing Month / Period</label><input className="custom-input" type="text" placeholder="e.g. Oct 2026" name="billingMonth" value={expenseDetails.billingMonth} onChange={handleDetailChange} /></div>
                        <div className="form-group grid-span-2"><label className="input-label">Invoice / Account Number</label><input className="custom-input" name="invoiceNumber" value={expenseDetails.invoiceNumber} onChange={handleDetailChange} /></div>
                    </>
                );
            case 'Maintenance & Repairs':
                return (
                    <>
                        <div className="form-group">
                            <label className="input-label">Repair Category *</label>
                            <select className="swal2-select custom-select" name="repairType" value={expenseDetails.repairType} onChange={handleDetailChange}>
                                <option value="Equipment / IT">Equipment / IT</option>
                                <option value="Facility / Office">Facility / Office</option>
                                <option value="Vehicle">Vehicle</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="input-label">Warranty Included?</label>
                            <select className="swal2-select custom-select" name="warrantyIncluded" value={expenseDetails.warrantyIncluded} onChange={handleDetailChange}>
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                            </select>
                        </div>
                        <div className="form-group grid-span-2"><label className="input-label">Service Provider / Technician Name *</label><input className="custom-input" name="serviceProvider" value={expenseDetails.serviceProvider} onChange={handleDetailChange} required /></div>
                    </>
                );
            case 'Fuel Expense (Car / Bike)':
                return (
                    <>
                        <div className="form-group"><label className="input-label">Vehicle Type *</label><select className="swal2-select custom-select" name="vehicleType" value={expenseDetails.vehicleType} onChange={handleDetailChange}><option value="Car">Car</option><option value="Bike">Bike</option><option value="Auto">Auto</option></select></div>
                        <div className="form-group"><label className="input-label">Vehicle Number *</label><input className="custom-input" name="vehicleNumber" value={expenseDetails.vehicleNumber} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Travel From *</label><input className="custom-input" name="travelFrom" value={expenseDetails.travelFrom} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Travel To *</label><input className="custom-input" name="travelTo" value={expenseDetails.travelTo} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Odometer (Before) *</label><input className="custom-input" type="number" name="odometerBefore" value={expenseDetails.odometerBefore} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Odometer (After) *</label><input className="custom-input" type="number" name="odometerAfter" value={expenseDetails.odometerAfter} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">KM Traveled (Auto)</label><input className="custom-input readonly-input" type="number" name="kmTraveled" value={expenseDetails.kmTraveled} readOnly /></div>
                        <div className="form-group grid-span-2"><label className="input-label">Purpose of Travel *</label><input className="custom-input" name="purpose" value={expenseDetails.purpose} onChange={handleDetailChange} required /></div>
                    </>
                );
            case 'Food Expense':
                return (
                    <>
                        <div className="form-group grid-span-2"><label className="input-label">Restaurant / Platform Name *</label><input className="custom-input" name="restaurantName" placeholder="e.g. Zomato, Swiggy" value={expenseDetails.restaurantName} onChange={handleDetailChange} required /></div>
                        <div className="form-group grid-span-2"><label className="input-label">Food Items Ordered *</label><RichTextEditor rows={2} name="foodItemsOrdered" ariaLabel="Food items ordered" value={expenseDetails.foodItemsOrdered} onChange={(v) => setExpenseDetails((d) => ({ ...d, foodItemsOrdered: v }))} required /></div>
                        <div className="form-group"><label className="input-label">Number of People</label><input className="custom-input" type="number" name="numberOfPeople" value={expenseDetails.numberOfPeople} onChange={handleDetailChange} /></div>
                    </>
                );
            case 'Travel Expense':
                return (
                    <>
                        <div className="form-group"><label className="input-label">Travel Mode *</label><select className="swal2-select custom-select" name="travelMode" value={expenseDetails.travelMode} onChange={handleDetailChange}><option value="Flight">Flight</option><option value="Train">Train</option><option value="Taxi / Cab">Taxi / Cab</option><option value="Bus">Bus</option></select></div>
                        <div className="form-group"><label className="input-label">Distance (KM) - If Road Travel</label><input className="custom-input" type="number" name="distanceKm" value={expenseDetails.distanceKm} onChange={handleDetailChange} /></div>
                        <div className="form-group"><label className="input-label">Travel From *</label><input className="custom-input" name="travelFrom" value={expenseDetails.travelFrom} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Travel To *</label><input className="custom-input" name="travelTo" value={expenseDetails.travelTo} onChange={handleDetailChange} required /></div>
                        <div className="form-group grid-span-2"><label className="input-label">Booking Reference / PNR</label><input className="custom-input" name="bookingReference" value={expenseDetails.bookingReference} onChange={handleDetailChange} /></div>
                        <div className="form-group grid-span-2"><label className="input-label">Purpose of Travel *</label><input className="custom-input" name="purpose" value={expenseDetails.purpose} onChange={handleDetailChange} required /></div>
                    </>
                );
            case 'Accommodation':
                return (
                    <>
                        <div className="form-group grid-span-2"><label className="input-label">Hotel / Property Name *</label><input className="custom-input" name="hotelName" value={expenseDetails.hotelName} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">City *</label><input className="custom-input" name="city" value={expenseDetails.city} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Booking Reference</label><input className="custom-input" name="bookingReference" value={expenseDetails.bookingReference} onChange={handleDetailChange} /></div>
                        <div className="form-group"><label className="input-label">Check-In Date *</label><input className="custom-input" type="date" name="checkInDate" value={expenseDetails.checkInDate} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Check-Out Date *</label><input className="custom-input" type="date" name="checkOutDate" value={expenseDetails.checkOutDate} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Number of Nights (Auto)</label><input className="custom-input readonly-input" type="number" name="numberOfNights" value={expenseDetails.numberOfNights} readOnly /></div>
                    </>
                );
            case 'Regular Office Expense':
                return (
                    <>
                        <div className="form-group"><label className="input-label">Vendor Name *</label><input className="custom-input" name="vendorName" value={expenseDetails.vendorName} onChange={handleDetailChange} required /></div>
                        <div className="form-group"><label className="input-label">Billing Cycle *</label><select className="swal2-select custom-select" name="billingCycle" value={expenseDetails.billingCycle} onChange={handleDetailChange}><option value="One-Time">One-Time</option><option value="Monthly">Monthly</option><option value="Annual">Annual</option></select></div>
                        <div className="form-group grid-span-2"><label className="input-label">Expense Description *</label><RichTextEditor rows={2} name="expenseDescription" ariaLabel="Expense description" value={expenseDetails.expenseDescription} onChange={(v) => setExpenseDetails((d) => ({ ...d, expenseDescription: v }))} required /></div>
                    </>
                );
            default: return null;
        }
    };

    if (loading) return <div className="main-content">Loading Data...</div>;

    return (
        <div className="profile-container fade-in">
            <div className="page-header-left">
                <button className="gts-btn warning btn-small m-0" onClick={() => goBack()}>
                    <FontAwesomeIcon icon={faArrowLeft} className="btn-icon" /> Back
                </button>
                <h1 className="page-title header-no-margin">Edit Expense Details</h1>
            </div>

            <div className="expense-form-card">
                
                {expenseStatus === 'Returned' && adminFeedback && (
                    <div className="alert-message warning mb-20" style={{ padding: '12px', borderRadius: '8px', fontSize: '13px', background: 'light-dark(#fef2f2, var(--theme-danger-soft))', border: '1px solid light-dark(#fca5a5, var(--theme-border-strong, #4a4e55))', color: 'light-dark(#991b1b, var(--theme-danger-ink))' }}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '6px' }} /> 
                        <strong>Returned for Correction:</strong> {adminFeedback}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="profile-form">

                    <div className="expense-form-section">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faInfoCircle} /> General Information
                        </div>

                        <div className="expense-grid">

                            <div className="form-group grid-span-2">
                                <label className="input-label">Expense Type</label>
                                <div className="expense-type-toggle">
                                    <label className={formData.expenseType === 'Project Expense' ? 'active' : ''}>
                                        <input type="radio" name="expenseType" value="Project Expense" onChange={handleMainChange} /> Project Expense
                                    </label>
                                    <label className={formData.expenseType === 'Regular Office Expense' ? 'active' : ''}>
                                        <input type="radio" name="expenseType" value="Regular Office Expense" onChange={handleMainChange} /> Regular Office
                                    </label>
                                </div>
                            </div>

                            {formData.expenseType === 'Project Expense' && (
                                <div className="form-group">
                                    <label className="input-label">Project Name *</label>
                                    {/* 👇 NEW: Search Select for Project */}
                                    <SearchSelect 
                                        options={projectsList}
                                        recentKey="project"
                                        value={formData.projectName}
                                        onChange={(val) => setFormData({ ...formData, projectName: val })}
                                        placeholder="Search Project..."
                                        valueKey="name" 
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label className="input-label">Expense Category *</label>
                                <select className="swal2-select custom-select" name="category" value={formData.category} onChange={handleMainChange} style={{ borderColor: '#215D7B' }}>
                                    <option value="Product / Item Purchase">Product / Item Purchase</option>
                                    <option value="Utility / Bills">Utility / Bills</option>
                                    <option value="Maintenance & Repairs">Maintenance & Repairs</option>
                                    <option value="Fuel Expense (Car / Bike)">Fuel Expense (Car / Bike)</option>
                                    <option value="Food Expense">Food Expense</option>
                                    <option value="Travel Expense">Travel Expense</option>
                                    <option value="Accommodation">Accommodation</option>
                                    {formData.expenseType === 'Project Expense' && <option value="Participant Payment">Participant Payment</option>}
                                    <option value="Vendor Payment">Vendor Payment</option>
                                    {formData.expenseType === 'Regular Office Expense' && <option value="Regular Office Expense">Regular Office Expense</option>}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="input-label">
                                    {formData.category === 'Vendor Payment' ? 'Date of Invoice *' : 'Date of Transaction *'}
                                </label>
                                <input className="custom-input" type="date" name="expenseDate" required value={formData.expenseDate} onChange={handleMainChange} />
                            </div>

                            <div className="form-group">
                                <label className="input-label"><FontAwesomeIcon icon={faRupeeSign} /> Amount (₹) *</label>
                                <input className="custom-input" type="number" name="amount" required placeholder="5000" value={formData.amount} onChange={handleMainChange} />
                            </div>

                            <div className="form-group">
                                <label className="input-label">GST Number (Optional)</label>
                                <input className="custom-input" type="text" name="gstNumber" placeholder="e.g. 29GGGGG1314R9Z6" value={expenseDetails.gstNumber} onChange={handleDetailChange} style={{ textTransform: 'uppercase' }} />
                            </div>

                            <div className="form-group grid-span-2" style={{ marginTop: '5px', marginBottom: '5px' }}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none' }}>
                                    <div style={{ position: 'relative', width: '44px', height: '24px', background: formData.isCompanyPayment ? '#16a34a' : 'light-dark(#cbd5e1, var(--theme-info-soft, #1d2c3a))', borderRadius: '24px', transition: 'background 0.3s ease' }}>
                                        <div style={{ position: 'absolute', top: '2px', left: formData.isCompanyPayment ? '22px' : '2px', width: '20px', height: '20px', background: 'light-dark(white, var(--theme-surface))', borderRadius: '50%', transition: 'left 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                                    </div>
                                    <span style={{ fontWeight: '600', color: 'light-dark(#334155, var(--theme-ink-soft))', fontSize: '14px' }}>Is company Account ?</span>
                                    <input type="checkbox" name="isCompanyPayment" checked={formData.isCompanyPayment} onChange={handleMainChange} style={{ display: 'none' }} />
                                </label>
                            </div>

                            {!formData.isCompanyPayment && (
                                <div className="form-group">
                                    <label className="input-label"><FontAwesomeIcon icon={faCreditCard} /> Payment Source (Who paid?) *</label>
                                    {/* 👇 NEW: Search Select for Employee */}
                                    <SearchSelect 
                                        options={getPaymentSourceOptions()}
                                        recentKey="expense-payer"
                                        value={formData.paymentSourceId}
                                        onChange={(val) => setFormData({ ...formData, paymentSourceId: val })}
                                        placeholder="Search by name..."
                                        secondaryKey="role"
                                        icon={faCreditCard}
                                    />
                                </div>
                            )}

                            <div className="form-group grid-span-2">
                                <label className="input-label"><FontAwesomeIcon icon={faTags} /> Description / Tags *</label>
                                <input className="custom-input" type="text" name="descriptionTags" required placeholder="e.g. N95 Mask, Train Ticket, Client Dinner" value={formData.descriptionTags} onChange={handleMainChange} />
                            </div>
                        </div>
                    </div>

                    <div className="expense-form-section">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faListAlt} /> {formData.category} Details
                        </div>
                        <div className="expense-grid">
                            {renderCategoryFields()}
                        </div>
                    </div>

                    <div className="expense-form-section mb-0">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faPaperclip} /> Attachments & Proof
                        </div>

                        {(existingFiles.paymentScreenshotUrls.length > 0 || existingFiles.expenseMediaUrls.length > 0) && (
                            <div className="alert-message warning mb-20" style={{ padding: '12px', borderRadius: '8px', fontSize: '13px', background: 'light-dark(#fffbeb, var(--theme-warning-soft))', border: '1px solid light-dark(#fef3c7, var(--theme-border-strong, #4a4e55))', color: 'light-dark(#b45309, var(--theme-warning-ink))' }}>
                                <FontAwesomeIcon icon={faInfoCircle} /> <strong>Note:</strong> Click the red ✕ on a file to remove it. New uploads will be added alongside any remaining files.
                            </div>
                        )}

                        <div className="expense-grid">
                            <div className="form-group expense-file-area">
                                <label className="input-label" style={{ borderBottom: '1px solid light-dark(#e2e8f0, var(--theme-border, #33363b))', paddingBottom: '10px', marginBottom: '15px' }}>
                                    Payment Screenshot / Bank Proof(s) (Optional)
                                </label>

                                {existingFiles.paymentScreenshotUrls?.length > 0 && (
                                    <div className="mb-15">
                                        <div className="text-small text-muted mb-10 fw-600">Currently Saved Files:</div>
                                        <div className="existing-file-gallery">
                                            {existingFiles.paymentScreenshotUrls.map((url, idx) => renderThumbnail(url, idx, 'Proof', (i) => removeExistingFile('paymentScreenshotUrls', i)))}
                                        </div>
                                    </div>
                                )}

                                <div className="upload-container-new">
                                    <div className="text-small text-muted mb-5 fw-600">Upload New Files (Optional):</div>
                                    <input 
                                        className="custom-file-input" 
                                        type="file" 
                                        multiple 
                                        accept="image/*,application/pdf" 
                                        onChange={e => handleFileChange(e, 'paymentScreenshots')} 
                                    />

                                    {isCompressing ? (
                                        <div className="file-success-badge mt-10" style={{ background: 'light-dark(#fef3c7, var(--theme-warning-soft))', color: 'light-dark(#b45309, var(--theme-warning-ink))' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Compressing...
                                        </div>
                                    ) : newFiles.paymentScreenshots.length > 0 && (
                                        <div className="file-chips-list">
                                            {newFiles.paymentScreenshots.map((f, i) => (
                                                <div key={i} className="file-chip">
                                                    <span className="file-chip-name">{f.name}</span>
                                                    <button type="button" className="file-chip-remove" onClick={() => removeNewFile('paymentScreenshots', i)}>✕</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group expense-file-area">
                                <label className="input-label" style={{ borderBottom: '1px solid light-dark(#e2e8f0, var(--theme-border, #33363b))', paddingBottom: '10px', marginBottom: '15px' }}>
                                    {getMediaLabel()}
                                </label>

                                {existingFiles.expenseMediaUrls?.length > 0 && (
                                    <div className="mb-15">
                                        <div className="text-small text-muted mb-10 fw-600">Currently Saved Files:</div>
                                        <div className="existing-file-gallery">
                                            {existingFiles.expenseMediaUrls.map((url, idx) => renderThumbnail(url, idx, 'Media', (i) => removeExistingFile('expenseMediaUrls', i)))}
                                        </div>
                                    </div>
                                )}

                                <div className="upload-container-new">
                                    <div className="text-small text-muted mb-5 fw-600">Upload New Files:</div>
                                    <input 
                                        className="custom-file-input" 
                                        type="file" 
                                        multiple 
                                        accept="image/*,video/*" 
                                        onChange={e => handleFileChange(e, 'expenseMedia')} 
                                        required={formData.category === 'Vendor Payment' && (!existingFiles.expenseMediaUrls || existingFiles.expenseMediaUrls.length === 0)}
                                    />

                                    {isCompressing ? (
                                        <div className="file-success-badge mt-10" style={{ background: 'light-dark(#fef3c7, var(--theme-warning-soft))', color: 'light-dark(#b45309, var(--theme-warning-ink))' }}>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Compressing...
                                        </div>
                                    ) : newFiles.expenseMedia.length > 0 && (
                                        <div className="file-chips-list">
                                            {newFiles.expenseMedia.map((f, i) => (
                                                <div key={i} className="file-chip">
                                                    <span className="file-chip-name">{f.name}</span>
                                                    <button type="button" className="file-chip-remove" onClick={() => removeNewFile('expenseMedia', i)}>✕</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="profile-actions mt-30 flex-col gap-15">

                        {saving && uploadProgress > 0 && (
                            <div className="upload-progress-container">
                                <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                                <span className="upload-progress-text">Uploading updates... {uploadProgress}%</span>
                            </div>
                        )}

                        <button type="submit" className="save-btn expense-submit-btn" disabled={saving || isCompressing}>
                            <FontAwesomeIcon icon={faSave} className="btn-icon" />
                            {saving ? 'Processing & Saving...' : isCompressing ? 'Compressing Files...' : expenseStatus === 'Returned' ? 'Resubmit for Approval' : 'Save Updates'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default EditExpense;